import { Box, BoxProps } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface GridPatternProps extends BoxProps {
  cellSize?: number;
  strokeWidth?: number;
  squareCount?: number;
  squares?: Array<[number, number]>;
  interactive?: boolean;
  /** How long each visited cell stays lit before fully fading, in ms */
  trailDuration?: number;
  /** Probability (0..1) that a hovered cell actually lights up. Default 0.35 */
  trailChance?: number;
}

const DEFAULT_SQUARES: Array<[number, number]> = [
  [4, 2],
  [9, 5],
  [14, 3],
  [18, 7],
  [22, 2],
  [27, 6],
  [3, 9],
  [11, 11],
  [16, 13],
  [20, 10],
  [26, 12],
  [30, 8],
];

interface TrailCell {
  col: number;
  row: number;
  id: number;
}

export function GridPattern({
  cellSize = 44,
  strokeWidth = 1,
  squareCount = 12,
  squares,
  interactive = true,
  trailDuration = 1400,
  trailChance = 0.35,
  ...props
}: GridPatternProps) {
  const id = useMemo(
    () => `grid-${Math.random().toString(36).slice(2, 9)}`,
    [],
  );

  const highlighted = (squares ?? DEFAULT_SQUARES).slice(0, squareCount);
  const containerRef = useRef<HTMLDivElement>(null);

  const [trail, setTrail] = useState<TrailCell[]>([]);
  const trailIdRef = useRef(0);
  const lastCellRef = useRef<{ col: number; row: number } | null>(null);

  const addCell = useCallback(
    (col: number, row: number) => {
      const last = lastCellRef.current;
      if (last && last.col === col && last.row === row) return;
      lastCellRef.current = { col, row };

      // Probabilistic activation
      if (Math.random() > trailChance) return;

      trailIdRef.current += 1;
      const cellId = trailIdRef.current;
      setTrail((prev) => {
        const next = [...prev, { col, row, id: cellId }];
        return next.length > 80 ? next.slice(-80) : next;
      });

      window.setTimeout(() => {
        setTrail((prev) => prev.filter((c) => c.id !== cellId));
      }, trailDuration);
    },
    [trailDuration, trailChance],
  );

  useEffect(() => {
    if (!interactive) return;
    if (typeof window === 'undefined') return;
    const reduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    if (reduced) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;

    const handleMove = (event: MouseEvent) => {
      const node = containerRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
        lastCellRef.current = null;
        return;
      }
      const col = Math.floor(x / cellSize);
      const row = Math.floor(y / cellSize);
      addCell(col, row);
    };

    window.addEventListener('mousemove', handleMove);
    return () => {
      window.removeEventListener('mousemove', handleMove);
    };
  }, [interactive, cellSize, addCell]);

  return (
    <Box
      ref={containerRef}
      pos="absolute"
      inset="0"
      pointerEvents="none"
      overflow="hidden"
      sx={{
        maskImage:
          'radial-gradient(circle at center, black 0%, black 35%, transparent 85%)',
        WebkitMaskImage:
          'radial-gradient(circle at center, black 0%, black 35%, transparent 85%)',
      }}
      {...props}
    >
      <Box
        as="svg"
        w="full"
        h="full"
        sx={{
          '@media (prefers-reduced-motion: no-preference)': {
            '.grid-square': {
              animation: 'gridSquarePulse 6s ease-in-out infinite',
            },
            '.grid-trail': {
              animation: `gridTrailFade ${trailDuration}ms ease-out forwards`,
            },
            '@keyframes gridSquarePulse': {
              '0%, 100%': { opacity: 0 },
              '50%': { opacity: 1 },
            },
            '@keyframes gridTrailFade': {
              '0%': { opacity: 0.1 },
              '25%': { opacity: 0.1 },
              '100%': { opacity: 0 },
            },
          },
        }}
      >
        <defs>
          <pattern
            id={id}
            width={cellSize}
            height={cellSize}
            patternUnits="userSpaceOnUse"
          >
            <path
              d={`M ${cellSize} 0 L 0 0 0 ${cellSize}`}
              fill="none"
              stroke="var(--chakra-colors-line-strong)"
              strokeWidth={strokeWidth}
            />
          </pattern>
        </defs>

        <rect width="100%" height="100%" fill={`url(#${id})`} />

        <g>
          {highlighted.map(([col, row], i) => (
            <rect
              key={`sq-${i}`}
              className="grid-square"
              x={col * cellSize + strokeWidth}
              y={row * cellSize + strokeWidth}
              width={cellSize - strokeWidth}
              height={cellSize - strokeWidth}
              fill="var(--chakra-colors-accent-dim)"
              style={{
                animationDelay: `${(i * 0.55) % 6}s`,
              }}
            />
          ))}
        </g>

        <g>
          {trail.map((cell) => (
            <rect
              key={cell.id}
              className="grid-trail"
              x={cell.col * cellSize + strokeWidth}
              y={cell.row * cellSize + strokeWidth}
              width={cellSize - strokeWidth}
              height={cellSize - strokeWidth}
              fill="var(--chakra-colors-accent-500)"
            />
          ))}
        </g>
      </Box>
    </Box>
  );
}
